"use client";

import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

function ArchiveBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="h-5 w-48 animate-pulse rounded-md bg-muted/60" />
    );
  }

  if (error || links.length === 0) return null;

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-blue-900/70">
        {links.map((link, i) => (
          <BreadcrumbItem key={link.href} data-index={i}>
            {i === lastIndex ? (
              <BreadcrumbPage className="text-blue-900 font-default font-medium">
                {link.title}
              </BreadcrumbPage>
            ) : (
              <>
                <BreadcrumbLink
                  href={link.href}
                  className="hover:text-blue-600 transition-colors font-default"
                >
                  {link.title}
                </BreadcrumbLink>
                <BreadcrumbSeparator />
              </>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleContent() {
  const { links, loading } = useShopBreadcrumbs();
  const titleReveal = useRevealOnScroll<HTMLDivElement>({ threshold: 0.1 });
  const subtitleReveal = useRevealOnScroll<HTMLDivElement>({ threshold: 0.1 });

  const archiveTitle =
    !loading && links.length > 0
      ? links[links.length - 1].title
      : "Our Wild-Caught Selection";

  return (
    <div className="relative grid grid-cols-12 gap-6 md:gap-10">
      {}
      <div
        aria-hidden="true"
        className="hidden md:block absolute -top-6 right-0 h-24 w-24 rounded-lg bg-blue-600/10 blur-2xl"
      />
      <div
        aria-hidden="true"
        className="hidden md:block absolute top-1/2 left-1/3 h-32 w-32 rounded-lg bg-blue-800/20 blur-3xl"
      />

      {}
      <div
        ref={subtitleReveal.ref}
        className={`col-span-12 md:col-span-10 md:col-start-2 transition-all duration-700 ease-out ${
          subtitleReveal.isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-3"
        }`}
      >
        <ArchiveBreadcrumbsConsumer />
      </div>

      {}
      <div
        ref={titleReveal.ref}
        className={`col-span-12 md:col-span-9 md:col-start-2 transition-all duration-1000 ease-out ${
          titleReveal.isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-6"
        }`}
      >
        <div className="flex items-end gap-4">
          <span
            aria-hidden="true"
            className="hidden sm:block h-px w-12 md:w-20 bg-blue-900/40 mb-6 md:mb-8"
          />
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-blue-700 font-default mb-4 md:mb-6">
            Heritage Catch
          </p>
        </div>

        <h1 className="font-default text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.05] tracking-tight text-blue-900">
          {archiveTitle}
        </h1>

        {}
        <div className="mt-8 md:mt-10 flex items-center gap-6">
          <span
            aria-hidden="true"
            className="h-px flex-1 max-w-[120px] bg-blue-600/60"
          />
          <span className="text-sm italic font-default text-blue-800">
            Built by Hand. Proven by Time.
          </span>
        </div>
      </div>

      {}
      <div
        className={`hidden md:block md:col-span-2 md:col-start-11 self-end transition-all duration-1000 delay-200 ease-out ${
          titleReveal.isVisible
            ? "opacity-100 translate-x-0"
            : "opacity-0 translate-x-4"
        }`}
      >
        <div className="border-l border-blue-900/20 pl-4">
          <p className="text-xs font-default uppercase tracking-widest text-blue-900">
            Miss Yvonne
          </p>
          <p className="text-xs font-default text-blue-700/70 mt-1">
            Ocean Springs
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Archive() {
  return (
    <section data-section-id="4903"
      id="archive-title"
      className="relative w-full bg-slate-50 text-blue-900 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-20 md:py-28 lg:py-32">
        <ShopBreadcrumbsProvider>
          <ArchiveTitleContent />
        </ShopBreadcrumbsProvider>
      </div>

      {}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent"
      />
    </section>
  );
}